/**
 * useCardSave — the shared client-side "persist a payment method" primitive.
 *
 * Owns the Stripe SetupIntent wiring and the confirm-setup-intent / save-token flow
 * that was duplicated byte-for-byte across the booking, subscription, and
 * account-setup card-entry flows. The intent is a SINGLE multi-method SetupIntent —
 * the server resolves its `payment_method_types` (card, or card + bank for the
 * native Payment Element accordion), so this hook no longer owns a card/bank toggle
 * or a second bank intent. Callers keep their own provider detection, user/club
 * sources, guards, state, and post-save action — this hook only owns the identical
 * inner primitive; {@link CardEntryForm} renders it.
 *
 * @module features/payment/hooks/useCardSave
 */

import { useCallback } from 'react';
import { useStripeSetupIntent, type StripeSetupIntent } from './useStripeSetupIntent';
import { useConfirmSetupIntent } from './useConfirmSetupIntent';
import { useSavePaymentMethod } from './useSavePaymentMethod';

/** The card provider a page has detected, or `null` when none is configured. */
export type CardSaveProvider = 'stripe' | 'square';

export interface UseCardSaveParams {
  /** Numeric user (or guest) id the method is saved for. */
  userId: number | undefined;
  /** Club the method is saved against (Square token path). */
  clubId: number | undefined;
  /** Tenant handle for cache invalidation. */
  tenantHandle: string | undefined;
  /** Detected provider, or `null` when none is configured. */
  paymentProvider: CardSaveProvider | null;
  /**
   * Gate for fetching the Stripe SetupIntent — the page decides when its Stripe
   * form is actually visible (mirrors each page's prior `enabled` arg).
   */
  stripeSetupEnabled: boolean;
}

export interface UseCardSaveResult {
  /** The (multi-method) SetupIntent the Payment Element renders + confirms against. */
  stripeSetupIntent: StripeSetupIntent | null;
  stripeSetupIntentLoading: boolean;
  stripeSetupIntentError: string | null;
  retryStripeSetupIntent: () => void;
  /**
   * Persist the instrument and return its database id.
   * - Stripe: confirm the client-confirmed SetupIntent (card or bank) via
   *   `/confirm`. On failure, mints a fresh SetupIntent (the used one is single-use)
   *   and rethrows.
   * - Square: POST the nonce to `/token`.
   *
   * Throws on any failure (including a missing db id). Callers wrap it with their
   * own guards + `reportPaymentClientError('save_card', …)` catch.
   */
  saveCard: (providerToken: string) => Promise<number>;
}

export function useCardSave({
  userId,
  clubId,
  tenantHandle,
  paymentProvider,
  stripeSetupEnabled,
}: UseCardSaveParams): UseCardSaveResult {
  const {
    setupIntent: stripeSetupIntent,
    isLoading: stripeSetupIntentLoading,
    error: stripeSetupIntentError,
    retry: retryStripeSetupIntent,
  } = useStripeSetupIntent(userId, stripeSetupEnabled);

  const confirmSetupIntent = useConfirmSetupIntent({ tenantHandle: tenantHandle || undefined });
  const savePaymentMethod = useSavePaymentMethod({ tenantHandle: tenantHandle || undefined });

  const saveCard = useCallback(
    async (providerToken: string): Promise<number> => {
      if (userId == null) {
        throw new Error('User ID not found. Please restart and try again.');
      }

      let databaseId: number | undefined;
      if (paymentProvider === 'stripe') {
        try {
          const confirmResult = await confirmSetupIntent.mutateAsync({
            userId,
            setupIntentId: providerToken, // seti_… from the client-side confirmSetup
          });
          databaseId = confirmResult.payload?.paymentMethodId;
        } catch (confirmErr) {
          // The SetupIntent was confirmed client-side (single-use, now consumed),
          // but persisting it failed. Fetch a fresh intent so the next attempt can
          // re-confirm instead of reusing the spent one.
          retryStripeSetupIntent();
          throw confirmErr;
        }
      } else {
        // Defend the contract: the else-branch is reached for 'square' OR null.
        // Fail loud on null rather than POSTing `provider: null` (drops the `!`).
        if (paymentProvider !== 'square') {
          throw new Error('Payment provider not detected.');
        }
        if (clubId == null) {
          throw new Error('Club ID not found. Please refresh the page.');
        }
        const result = await savePaymentMethod.mutateAsync({
          userId,
          request: {
            provider: paymentProvider,
            clubId,
            paymentMethodToken: providerToken, // Square nonce (cnon:xxx)
          },
        });
        databaseId = result.payload?.paymentMethodId;
      }

      if (!databaseId) {
        throw new Error('Failed to retrieve payment method database ID');
      }
      return databaseId;
    },
    [userId, clubId, paymentProvider, confirmSetupIntent, savePaymentMethod, retryStripeSetupIntent]
  );

  return {
    stripeSetupIntent,
    stripeSetupIntentLoading,
    stripeSetupIntentError,
    retryStripeSetupIntent,
    saveCard,
  };
}
